import React, { useState } from "react"
import { Modal, Button, Text, ProgressIndicator } from "@quorumus/plum"
import { Spoiler, Stack } from "@mantine/core"
import { SparklesFarFAIcon, ArrowsRotateFAIcon } from "@quorumus/plum/dist/components/Icons"
import { PersonalizationProfileSelect } from "@/components/PersonalizationProfileSelect/PersonalizationProfileSelect"
import { usePersonalizedSummaryGeneration } from "./hooks/usePersonalizedSummaryGeneration"
import classes from "./PersonalizedSummary.module.css"
import { usePersonalizedSummary } from "./hooks/usePersonalizedSummary"
import { usePersonalizedSummaryApplication } from "./hooks/usePersonalizedSummaryApplication"
import Markdown from "@/components/CopilotAnalyze/Message/Markdown"
import { FeedbackActions } from "./FeedbackActions"
import { useNavigate } from "react-router-dom"

type ModalState = "closed" | "profile-selection" | "summary-generated"

// export const PersonalizedSummary: React.FC<PersonalizedSummaryProps> = ({ boardId, profileId, billId }) => {
export const PersonalizedSummary = ({ boardId, profileId, billId, nextBillId }) => {
    const navigate = useNavigate()
    const [modalState, setModalState] = useState<ModalState>("closed")
    const [selectedProfileId, setSelectedProfileId] = useState<string | null>(profileId)

    const generateMutation = usePersonalizedSummaryGeneration()
    const applyMutation = usePersonalizedSummaryApplication()
    const personalizationQuery = usePersonalizedSummary({
        billId,
        boardId,
        profileId: selectedProfileId,
        nextBillId,
        enabled: modalState === "closed",
    })

    const handleGenerateClick = () => {
        // If in AI Bill Tracking context but no profile assigned, navigate to board config
        if (boardId && !selectedProfileId) {
            navigate(`/ai_bill_tracker/${boardId}/edit/?step=configure`)
            return
        }

        // If in AI Bill Tracking context with profile, refetch to trigger backend auto-generation
        if (boardId && selectedProfileId) {
            personalizationQuery.refetch()
        } else {
            // Bill Profile: Show modal as before
            setModalState("profile-selection")
        }
    }

    const handleCloseModal = () => {
        setModalState("closed")
        generateMutation.reset()
    }

    const handleProfileGenerate = async () => {
        if (!selectedProfileId) return

        setModalState("summary-generated")

        try {
            generateMutation.mutate({ billId, profileId: selectedProfileId, boardId })
        } catch (error) {
            console.error("Failed to generate summary:", error)
        }
    }

    const handleApplySummary = async () => {
        if (!generateMutation.data || !selectedProfileId) return

        try {
            await applyMutation.mutateAsync({
                boardId,
                billId,
                summary: generateMutation.data.summary,
                profileId: selectedProfileId,
            })
            handleCloseModal()
            personalizationQuery.refetch()
        } catch (error) {
            console.error("Failed to apply summary:", error)
        }
    }

    if (personalizationQuery.isFetching) {
        return (
            <div className={classes.initialContainer}>
                <ProgressIndicator size="sm" />
                {boardId && <Text size="sm">Generating personalized summary...</Text>}
            </div>
        )
    }

    return (
        <>
            <div className={classes.initialContainer}>
                {personalizationQuery.data?.summary ? (
                    <>
                        <Spoiler maxHeight={120} showLabel="See more" hideLabel="See less">
                            <Stack spacing="xs">
                                <Markdown>{personalizationQuery.data.summary}</Markdown>
                            </Stack>
                        </Spoiler>
                        <div className={classes.summaryFooter}>
                            {personalizationQuery.data.createdAt && (
                                <Text size="sm" color="dimmed">
                                    {`Generated on ${new Intl.DateTimeFormat("en-US", {
                                        day: "numeric",
                                        month: "short",
                                        hour: "2-digit",
                                        minute: "2-digit",
                                        hour12: false,
                                    }).format(
                                        new Date(personalizationQuery.data.createdAt),
                                    )} by "${personalizationQuery.data.profileName}"`}
                                </Text>
                            )}
                            <Button
                                variant="secondary"
                                size="sm"
                                leftIcon={ArrowsRotateFAIcon}
                                onClick={handleGenerateClick}
                                className={classes.generateButton}
                            >
                                Regenerate
                            </Button>
                        </div>
                    </>
                ) : (
                    <>
                        <Text size="sm" className={classes.initialText}>
                            It looks like you don't have a personalized summary yet.
                        </Text>
                        <Button
                            variant="primary"
                            size="sm"
                            leftIcon={boardId && !selectedProfileId ? undefined : SparklesFarFAIcon}
                            onClick={handleGenerateClick}
                            className={classes.generateButton}
                            data-plum-id="generate-personalized-ai-summary-initial"
                        >
                            {boardId && !selectedProfileId ? "Manage Personalization" : "Generate"}
                        </Button>
                    </>
                )}
                <div className={classes.divider} />
            </div>
            <Modal
                opened={modalState === "profile-selection" || modalState === "summary-generated"}
                onClose={handleCloseModal}
                title="Select a personalization"
                size="600px"
                classNames={{ header: classes.modalHeader }}
                className={classes.modal}
            >
                {/* Modal content only shows for Bill Profile context (no boardId) */}
                {!boardId && (
                    <div className={classes.modalContent}>
                        <div className={classes.profileSelectRow}>
                            <div className={classes.profileSelectContainer}>
                                <PersonalizationProfileSelect
                                    label="Personalization"
                                    value={selectedProfileId?.toString()}
                                    onChange={setSelectedProfileId}
                                    onNewProfile={handleCloseModal}
                                />
                            </div>
                            <Button
                                variant="primary"
                                size="sm"
                                leftIcon={SparklesFarFAIcon}
                                onClick={handleProfileGenerate}
                                loading={generateMutation.isLoading}
                            >
                                Generate
                            </Button>
                        </div>
                        {generateMutation.data && (
                            <>
                                <div className={classes.divider} />
                                <Text className={classes.previewTitle}>Preview</Text>
                                <div className={classes.summaryPreview}>
                                    <div className={classes.summaryContent}>
                                        <div className={classes.summaryTitle}>Personalized Summary</div>
                                        <Spoiler maxHeight={120} showLabel="See more" hideLabel="See less">
                                            <Stack spacing="xs">
                                                <Markdown>{generateMutation.data.summary}</Markdown>
                                                {generateMutation.data.createdAt && (
                                                    <Text size="sm" color="dimmed">
                                                        Updated:{" "}
                                                        {new Date(generateMutation.data.createdAt).toLocaleDateString()}
                                                    </Text>
                                                )}
                                            </Stack>
                                        </Spoiler>
                                    </div>
                                </div>
                                <div className={classes.feedbackRow}>
                                    <div className={classes.feedbackButtons}>
                                        <FeedbackActions
                                            summary={generateMutation?.data?.summary}
                                            billId={billId}
                                            profileId={selectedProfileId}
                                        />
                                    </div>

                                    <Button
                                        variant="accent"
                                        size="sm"
                                        onClick={handleApplySummary}
                                        loading={applyMutation.isLoading}
                                        disabled={applyMutation.isLoading}
                                    >
                                        Submit and Apply
                                    </Button>
                                </div>
                            </>
                        )}
                    </div>
                )}
            </Modal>
        </>
    )
}
